import { Spinner } from '@egw/components'
import { useTranslation } from '@egw/i18n'
import { ExclamationCircleIcon } from '@heroicons/react/20/solid'
import { ChevronRightIcon } from '@heroicons/react/24/outline'
import { useEffect, useState, type FC } from 'react'
import { UpdateActionType, type InstitutionUpdateSelectionProps } from '../../../types/plaid-multi-link'

/**
 * Institution update selection component for Plaid Multi Link flow
 */
export const InstitutionUpdateSelection: FC<InstitutionUpdateSelectionProps> = ({
  onUpdateActionSelection,
  showRelink,
  isChaseBank,
}) => {
  const [updateAction, setUpdateAction] = useState<UpdateActionType | undefined>(undefined)
  const { t } = useTranslation('features-external-accounts')

  const isDisabled = updateAction !== undefined

  useEffect(() => {
    const unsubscribe = (): void => {
      setUpdateAction(undefined)
    }
    return unsubscribe
  }, [updateAction, onUpdateActionSelection])

  return (
    <div
      className="flex flex-col"
      data-testid="plaid-multi-link-account-update-selection"
      role="form"
      aria-labelledby="account-update-selection-title"
    >
      {!isChaseBank && (
        <button
          key={UpdateActionType.UPDATING_INSTITUTION}
          type="button"
          data-testid="plaid-multi-link-manage-accounts-button"
          disabled={isDisabled}
          className={`w-full text-body flex items-center justify-between py-4 px-2 text-base border-list ${
            isDisabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'
          }`}
          onClick={() => {
            setUpdateAction(UpdateActionType.UPDATING_INSTITUTION)
            onUpdateActionSelection(UpdateActionType.UPDATING_INSTITUTION)
          }}
          aria-describedby={`${UpdateActionType.UPDATING_INSTITUTION}-description`}
        >
          <span className="typo-primary-base-regular text-text-body">{t('linked_accounts.manage_accounts')}</span>
          {updateAction === UpdateActionType.UPDATING_INSTITUTION ? (
            <div className="flex items-center gap-2">
              <Spinner className="fill-icons-meta" />
            </div>
          ) : (
            <ChevronRightIcon className="text-text-meta w-5 h-5" />
          )}
        </button>
      )}

      <button
        key={UpdateActionType.REMOVE_INSTITUTION}
        type="button"
        data-testid="plaid-multi-link-remove-button"
        disabled={isDisabled}
        className={`w-full text-body flex items-center justify-between py-4 px-2 text-base border-list ${
          isDisabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'
        }`}
        onClick={() => {
          onUpdateActionSelection(UpdateActionType.REMOVE_INSTITUTION)
        }}
        aria-describedby={`${UpdateActionType.REMOVE_INSTITUTION}-description`}
      >
        <span className="typo-primary-base-regular text-text-body">{t('linked_accounts.remove_institution')}</span>
        {updateAction === UpdateActionType.REMOVE_INSTITUTION ? (
          <div className="flex items-center gap-2">
            <Spinner className="fill-icons-meta" />
          </div>
        ) : (
          <ChevronRightIcon className="text-text-meta w-5 h-5" />
        )}
      </button>

      {showRelink ? (
        <button
          key={UpdateActionType.RELINK_INSTITUTION}
          type="button"
          data-testid="plaid-multi-link-relink-button"
          disabled={isDisabled}
          className={`w-full flex flex-row items-center justify-between py-4 px-2 border-list ${
            isDisabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'
          }`}
          onClick={() => {
            setUpdateAction(UpdateActionType.RELINK_INSTITUTION)
            onUpdateActionSelection(UpdateActionType.RELINK_INSTITUTION)
          }}
          aria-describedby={`${UpdateActionType.RELINK_INSTITUTION}-description`}
        >
          <div className="flex flex-row items-center gap-3xs">
            <div className="rounded-full w-[26px] h-[26px] bg-red-200 flex items-center justify-center mr-xs">
              <ExclamationCircleIcon className="h-4 w-4 text-alert-300 self-center" />
            </div>
            <span>
              <p className="flex typo-primary-base-regular text-text-body justify-start">
                {t('linked_accounts.relink_institution')}
              </p>
              <p className="typo-primary-sm-regular text-text-body">
                {t('linked_accounts.institution_no_longer_connected')}
              </p>
            </span>
          </div>
          {updateAction === UpdateActionType.RELINK_INSTITUTION ? (
            <div className="flex items-center gap-2">
              <Spinner className="fill-icons-meta" />
            </div>
          ) : (
            <ChevronRightIcon className="text-text-meta w-5 h-5 group-hover:cursor-pointer" />
          )}
        </button>
      ) : null}

      {isChaseBank ? (
        <p className="py-xs px-2xs typo-primary-sm-regular text-text-body">
          {t('linked_accounts.manage_chase_bank_accounts')}
        </p>
      ) : null}
    </div>
  )
}
